import { useEffect, useRef, useState } from 'react'
import { usePioneerStore, TIER_CONFIG, PioneerTier } from '../store/pioneerStore'
import { ScreenWrapper, EightStar, EngravingDivider } from '../components/shared'

// ─── Props from GP1 App.tsx ───────────────────────────────────────────────────

interface Screen5Props {
  onShareClick?: (platform: string) => void
}

// ─── Certificate border SVG ───────────────────────────────────────────────────

const CertificateBorder = () => (
  <svg
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    viewBox="0 0 500 700"
    preserveAspectRatio="none"
    aria-hidden
  >
    <rect x="8" y="8" width="484" height="684" fill="none" stroke="#C9A34A" strokeWidth="1" strokeOpacity="0.5" />
    <rect x="18" y="18" width="464" height="664" fill="none" stroke="#C9A34A" strokeWidth="0.4" strokeOpacity="0.3" />
    {[{ x: 8, y: 8 }, { x: 492, y: 8 }, { x: 8, y: 692 }, { x: 492, y: 692 }].map((pos, i) => (
      <g key={i} transform={`translate(${pos.x}, ${pos.y})`}>
        <circle r="4" fill="#C9A34A" fillOpacity="0.7" />
        <circle r="8" fill="none" stroke="#C9A34A" strokeWidth="0.5" strokeOpacity="0.3" />
      </g>
    ))}
    <line x1="250" y1="8" x2="250" y2="22" stroke="#C9A34A" strokeWidth="0.5" strokeOpacity="0.4" />
    <line x1="250" y1="692" x2="250" y2="678" stroke="#C9A34A" strokeWidth="0.5" strokeOpacity="0.4" />
    <line x1="8" y1="350" x2="22" y2="350" stroke="#C9A34A" strokeWidth="0.5" strokeOpacity="0.4" />
    <line x1="492" y1="350" x2="478" y2="350" stroke="#C9A34A" strokeWidth="0.5" strokeOpacity="0.4" />
  </svg>
)

const ConstellationBg = () => (
  <svg
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.06, pointerEvents: 'none' }}
    viewBox="0 0 500 700"
    aria-hidden
  >
    {[[60,80],[440,100],[120,200],[380,180],[50,350],[460,320],[200,120],[310,90],[90,500],[420,480],[150,600],[350,620],[250,400],[70,250],[430,260]].map(([x,y],i) => (
      <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 1.5 : 1} fill="#C9A34A" />
    ))}
    <line x1="60" y1="80" x2="200" y2="120" stroke="#C9A34A" strokeWidth="0.3" />
    <line x1="200" y1="120" x2="310" y2="90" stroke="#C9A34A" strokeWidth="0.3" />
    <line x1="310" y1="90" x2="440" y2="100" stroke="#C9A34A" strokeWidth="0.3" />
    <line x1="120" y1="200" x2="250" y2="400" stroke="#C9A34A" strokeWidth="0.2" />
    <line x1="380" y1="180" x2="460" y2="320" stroke="#C9A34A" strokeWidth="0.2" />
  </svg>
)

// ─── Tier color palette (GP1 tier names) ─────────────────────────────────────

const TIER_PALETTE: Record<PioneerTier, { bg: string; accent: string; glow: string }> = {
  STELLIUM: {
    bg:     'linear-gradient(160deg, #0D0B1E 0%, #09081A 60%, #0A0820 100%)',
    accent: '#C9A34A',
    glow:   '#C9A34A33',
  },
  LUMINARY: {
    bg:     'linear-gradient(160deg, #12100E 0%, #09081A 60%, #100E09 100%)',
    accent: '#C9A34A',
    glow:   '#C9A34A28',
  },
  CELESTIAL: {
    bg:     'linear-gradient(160deg, #0E0D12 0%, #09081A 60%, #0D0D0E 100%)',
    accent: '#C9A34A',
    glow:   '#C9A34A1E',
  },
}

// ─── Share button ─────────────────────────────────────────────────────────────

function ShareButton({ onClick, icon, label }: { onClick: () => void; icon: string; label: string }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
        background: 'transparent', border: '1px solid #C9A34A33', color: '#C9A34A',
        padding: '10px 16px', cursor: 'pointer', borderRadius: 2, transition: 'all 0.2s', minWidth: 72,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#C9A34A11' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      <span style={{ fontSize: '1.2rem' }}>{icon}</span>
      <span style={{ fontFamily: '"DM Mono", monospace', fontSize: '0.55rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
        {label}
      </span>
    </button>
  )
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function Screen5Confirmation({ onShareClick }: Screen5Props) {
  // GP1 store uses pioneerSeat (not pioneerData)
  const { birthData, chartData, pioneerSeat } = usePioneerStore()
  const [visible, setVisible] = useState(false)
  const [starRevealed, setStarRevealed] = useState(false)
  const [serialRevealed, setSerialRevealed] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setTimeout(() => setVisible(true),      100)
    setTimeout(() => setStarRevealed(true),  700)
    setTimeout(() => setSerialRevealed(true),1200)
  }, [])

  if (!pioneerSeat || !chartData) return null

  const tier       = pioneerSeat.tier
  const tierConfig = TIER_CONFIG[tier]
  const palette    = TIER_PALETTE[tier]
  const name       = birthData?.name || 'Pioneer'

  function handleShare() {
    onShareClick?.('native')
    if (navigator.share) {
      navigator.share({
        title: 'I joined INFINI',
        text: `I just claimed Pioneer seat ${pioneerSeat!.serialNumber} at INFINI. My chart: ${chartData!.sun.sign} Sun · ${chartData!.moon.sign} Moon. The stars were always mine.`,
        url: 'https://joininfini.com',
      })
    }
  }

  function handleScreenshot() {
    onShareClick?.('screenshot')
    alert('Screenshot functionality — integrate html2canvas for production.')
  }

  return (
    <ScreenWrapper>
      {/* Full-page radial glow */}
      <div
        aria-hidden
        style={{
          position: 'fixed', inset: 0,
          background: `radial-gradient(ellipse at 50% 40%, ${palette.glow} 0%, transparent 65%)`,
          pointerEvents: 'none',
        }}
      />

      <div
        className="flex flex-col items-center justify-center min-h-screen px-6 py-12"
        style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.8s ease' }}
      >
        {/* Top announcement */}
        <div
          className="text-center mb-8"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(-16px)',
            transition: 'opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s',
          }}
        >
          <p style={{ fontFamily: '"DM Mono", monospace', fontSize: '0.62rem', letterSpacing: '0.28em', color: '#C9A34A', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            ✦ You are now a Pioneer ✦
          </p>
          <h1 style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(1.6rem, 5vw, 2.5rem)', fontWeight: 700, color: '#F5E6C8', lineHeight: 1.2 }}>
            Welcome to the cosmos,
            <br />
            <em style={{ color: '#C9A34A' }}>{name.split(' ')[0]}</em>
          </h1>
        </div>

        {/* ═══ THE CERTIFICATE CARD ═══ */}
        <div
          ref={cardRef}
          style={{
            position: 'relative', width: '100%', maxWidth: 440,
            background: palette.bg, padding: '52px 40px', textAlign: 'center',
            boxShadow: `0 0 80px ${palette.glow}, 0 0 160px ${palette.glow}`,
            transform: visible ? 'scale(1)' : 'scale(0.94)',
            transition: 'transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s',
          }}
        >
          <CertificateBorder />
          <ConstellationBg />

          <p style={{ fontFamily: '"DM Mono", monospace', fontSize: '0.55rem', letterSpacing: '0.35em', color: '#C9A34A55', textTransform: 'uppercase', marginBottom: '1.5rem' }}>
            I N F I N I
          </p>

          {/* Eight-pointed star */}
          <div style={{ marginBottom: '1rem', opacity: starRevealed ? 1 : 0, transform: starRevealed ? 'scale(1) rotate(0deg)' : 'scale(0.3) rotate(-45deg)', transition: 'opacity 0.7s ease, transform 0.7s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
            <EightStar size={44} color="#C9A34A" className="mx-auto" />
          </div>

          {/* Tier label */}
          <p style={{ fontFamily: '"DM Mono", monospace', fontSize: '0.62rem', letterSpacing: '0.3em', color: '#C9A34A', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
            {tierConfig.label} Pioneer
          </p>

          {/* Name */}
          <h2 style={{ fontFamily: '"Playfair Display", serif', fontSize: 'clamp(1.5rem, 4vw, 2rem)', fontStyle: 'italic', color: '#F5E6C8', fontWeight: 700, lineHeight: 1.2, marginBottom: '0.25rem' }}>
            {name}
          </h2>

          <EngravingDivider className="my-4" />

          {/* Serial number */}
          <div style={{ opacity: serialRevealed ? 1 : 0, transform: serialRevealed ? 'translateY(0)' : 'translateY(20px)', transition: 'opacity 0.8s ease, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
            <p style={{ fontFamily: '"DM Mono", monospace', fontSize: '0.52rem', letterSpacing: '0.25em', color: '#C9A34A55', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              Pioneer Serial
            </p>
            <p style={{ fontFamily: '"DM Mono", monospace', fontSize: 'clamp(1.8rem, 6vw, 2.8rem)', letterSpacing: '0.05em', color: '#C9A34A', fontWeight: 600, lineHeight: 1, textShadow: '0 0 30px #C9A34A66' }}>
              {pioneerSeat.serialNumber}
            </p>
            <p style={{ fontFamily: '"DM Mono", monospace', fontSize: '0.6rem', color: '#C9A34A55', letterSpacing: '0.1em', marginTop: '0.3rem' }}>
              Seat {String(pioneerSeat.seatNumber).padStart(4, '0')} of 0888
            </p>
          </div>

          <EngravingDivider className="my-4" />

          {/* Chart summary */}
          <div
            className="flex justify-center gap-6"
            style={{ opacity: serialRevealed ? 1 : 0, transition: 'opacity 0.8s ease 0.3s' }}
          >
            {[
              { label: 'Sun',    value: `${chartData.sun.glyph ?? ''} ${chartData.sun.sign}`.trim() },
              { label: 'Moon',   value: `${chartData.moon.glyph ?? ''} ${chartData.moon.sign}`.trim() },
              chartData.rising
                ? { label: 'Rising', value: `${chartData.rising.glyph ?? ''} ${chartData.rising.sign}`.trim() }
                : null,
            ]
              .filter(Boolean)
              .map((item) => (
                <div key={item!.label}>
                  <p style={{ fontFamily: '"DM Mono", monospace', fontSize: '0.5rem', letterSpacing: '0.18em', color: '#C9A34A55', textTransform: 'uppercase', marginBottom: 2 }}>
                    {item!.label}
                  </p>
                  <p style={{ fontFamily: '"Playfair Display", serif', fontSize: '0.88rem', color: '#F5E6C8CC' }}>
                    {item!.value}
                  </p>
                </div>
              ))}
          </div>

          <p style={{ marginTop: '2rem', fontFamily: '"DM Mono", monospace', fontSize: '0.5rem', letterSpacing: '0.18em', color: '#C9A34A33', textTransform: 'uppercase' }}>
            joininfini.com · Est. 2025
          </p>
        </div>

        {/* Screenshot prompt */}
        <p className="mt-5 text-center" style={{ fontFamily: '"DM Mono", monospace', fontSize: '0.6rem', letterSpacing: '0.15em', color: '#C9A34A66', textTransform: 'uppercase', opacity: serialRevealed ? 1 : 0, transition: 'opacity 0.8s ease 0.8s' }}>
          ↑ Screenshot this. It's yours forever.
        </p>

        {/* Share row */}
        <div className="flex gap-3 mt-5 flex-wrap justify-center" style={{ opacity: serialRevealed ? 1 : 0, transition: 'opacity 0.8s ease 1s' }}>
          <ShareButton icon="📲" label="Share"     onClick={handleShare} />
          <ShareButton icon="📸" label="Save card" onClick={handleScreenshot} />
        </div>

        {/* What happens next */}
        <div className="w-full max-w-md mt-8 p-5" style={{ background: '#C9A34A08', border: '1px solid #C9A34A22', borderRadius: 2, opacity: serialRevealed ? 1 : 0, transition: 'opacity 0.8s ease 1.2s' }}>
          <p style={{ fontFamily: '"DM Mono", monospace', fontSize: '0.58rem', letterSpacing: '0.2em', color: '#C9A34A', textTransform: 'uppercase', marginBottom: '0.75rem', textAlign: 'center' }}>
            What happens next
          </p>
          <ul className="flex flex-col gap-2">
            {[
              'Your Pioneer confirmation email is on its way.',
              'Your Name Card will be available once you create your INFINI account.',
              "Watch for early access invitations — you're first in line.",
              'The founding story begins with you.',
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2">
                <span style={{ fontFamily: '"DM Mono", monospace', fontSize: '0.6rem', color: '#C9A34A66', minWidth: 16, paddingTop: 1 }}>
                  {String(i + 1).padStart(2, '0')}.
                </span>
                <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.85rem', color: '#F5E6C8BB', lineHeight: 1.4 }}>
                  {step}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ScreenWrapper>
  )
}
